import React from "react";
import { HStack, Text, Tooltip } from "@chakra-ui/react";
import { faxSchemaTooltip, matchedTablesLabel } from "../../utils/faxMatchedTables";
import { schemaVisitLabel } from "../../utils/faxMatchedDates";

/**
 * Which schemas a fax actually reached.
 *
 * A fax arrives on one doctor's number, but when it matches a patient shared
 * across the clinic it is copied to that patient's chart in every schema of the
 * group (fan_out_fax_to_schema_group). The inbox entry showed none of that, so
 * there was no way to tell whether colleagues had it too — or that the entry
 * you are looking at is a copy of a fax that landed on someone else's number.
 *
 * `matched_schemas` comes from the backend (annotate_matched_schemas). Renders
 * nothing when the fax only reached the schema being viewed, so single-schema
 * clinics see no change.
 */
export default function FaxSchemaBadges({ fax, viewingSchema }) {
  const schemas = Array.isArray(fax?.matched_schemas) ? fax.matched_schemas : [];

  // Single schema: no schema chip — it would just repeat the inbox you are
  // already looking at — but the TABLES still matter. Whether a fax reached the
  // visit, the chart row, or both is the same question in a one-clinic inbox as
  // in a group, and it was only being answered in the group case.
  if (schemas.length < 2) {
    const only = schemas[0] || viewingSchema;
    const tables = matchedTablesLabel((fax?.matched_tables || {})[only]);
    if (!tables) return null;
    return (
      <HStack spacing={1} flexWrap="wrap" rowGap={1}>
        <Text fontSize="xs" color="gray.500">
          Matched in:
        </Text>
        <Text
          fontSize="xs"
          fontWeight="medium"
          color="purple.700"
          bg="purple.50"
          px={2}
          py={0.5}
          borderRadius="md"
          borderWidth="1px"
          borderColor="purple.200"
        >
          {tables} · {schemaVisitLabel(only, fax)}
        </Text>
      </HStack>
    );
  }

  return (
    <HStack spacing={1} flexWrap="wrap" rowGap={1}>
      <Text fontSize="xs" color="gray.500">
        Filed to:
      </Text>
      {schemas.map((name) => (
        <Tooltip
          key={name}
          // Also names WHICH table the fax matched in that schema — a visit in
          // notes-c, the chart row in the patients table, or both. The same fax
          // can differ per schema, which is why it is per chip.
          label={faxSchemaTooltip(name, viewingSchema, fax.matched_tables)}
        >
          <Text
            fontSize="xs"
            fontWeight={name === viewingSchema ? "bold" : "normal"}
            color={name === viewingSchema ? "purple.700" : "purple.500"}
            bg={name === viewingSchema ? "purple.100" : "purple.50"}
            px={2}
            py={0.5}
            borderRadius="md"
            borderWidth="1px"
            borderColor="purple.200"
          >
            {/* Each schema files the fax onto ITS OWN next visit, so the dates
                differ — showing them side by side is the only way to see where
                the fax actually landed for each clinic. "n/a" means that schema
                matched the chart row only, with no visit to point at. */}
            {name}: {schemaVisitLabel(name, fax)}
          </Text>
        </Tooltip>
      ))}
      {fax.is_group_copy && (
        <Tooltip label="This copy was filed here by the clinic-group fan-out; the fax arrived on another schema's number.">
          <Text
            fontSize="xs"
            color="gray.600"
            bg="gray.100"
            px={2}
            py={0.5}
            borderRadius="md"
          >
            copy
          </Text>
        </Tooltip>
      )}
    </HStack>
  );
}
